import React, { FC, useState } from 'react';
import { Controller } from 'react-hook-form';
import { FullScreenOutwardIcon } from '@src/svg';
import { Editor, EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import Underline from '@tiptap/extension-underline';
import ListItem from '@tiptap/extension-list-item';
import OrderedList from '@tiptap/extension-ordered-list';
import BulletList from '@tiptap/extension-bullet-list';
import Color from '@tiptap/extension-color';
import { BoldLineIcon } from '@svg/BoldLineIcon';
import { ItalicLineIcon } from '@svg/ItalicLineIcon';
import { ListLineIcon } from '@svg/ListLineIcon';
import { ListOrderedLineIcon } from '@svg/ListOrderedLineIcon';
import { RedoLineIcon } from '@svg/RedoLineIcon';
import { UnderlineLineIcon } from '@svg/UnderlineLineIcon';
import { UndoLineIcon } from '@svg/UndoLineIcon';
import InfoTooltip from '@uikit/Info/InfoTooltip/InfoTooltip';
import { ControlledTextEditorPropsType } from '@uikit/FormItems/constants';
import ControlledTextEditorModal from '@uikit/FormItems/ControlledItems/ControlledTextEditor/ControlledTextEditorModal';
import { rgbToHex } from '@src/utils';

const CustomTextStyle = TextStyle.extend({
  addAttributes() {
    return {
      fontSize: {
        default: '16',
        parseHTML: (element) =>
          element.style.fontSize.replace('px', '') || '16',
        renderHTML: (attributes) => {
          return { style: `font-size: ${attributes.fontSize}px` };
        },
      },
    };
  },
});

export const ControlledTextEditor: FC<ControlledTextEditorPropsType> = ({
  name,
  control,
  rules,
  title,
  disabled,
  info,
  placeholder = 'Write here...',
  height,
  hasFullScreen,
  dataTestId,
}) => {
  const [toggleFullScreenModal, setToggleFullScreenModal] =
    useState<boolean>(false);
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
    bulletList: false,
    orderedList: false,
    color: '#000000',
    fontSize: '16',
  });

  const fontSizes = [10, 12, 14, 16, 18, 20, 24, 28, 32, 36];

  const updateActiveStyles = (editor: Editor) => {
    setActiveStyles({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      color: rgbToHex(editor.getAttributes('textStyle').color) || '#000000',
      fontSize: editor.getAttributes('textStyle').fontSize || '16',
    });
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const editor = useEditor(
          {
            extensions: [
              StarterKit,
              CustomTextStyle,
              Underline,
              BulletList,
              OrderedList,
              ListItem,
              Color,
            ],
            content: field.value || placeholder,
            onUpdate: ({ editor }) => {
              field.onChange(editor.getHTML());
              updateActiveStyles(editor);
            },
            onSelectionUpdate: ({ editor }) => {
              updateActiveStyles(editor);
            },
          },
          [toggleFullScreenModal]
        );

        return (
          <div
            className={disabled ? 'input-editor disabled' : 'input-editor'}
            data-test-id={`${dataTestId}-controlled-text-editor-container`}
          >
            {toggleFullScreenModal && (
              <ControlledTextEditorModal
                name={name}
                control={control}
                title={title}
                closeModal={() => setToggleFullScreenModal(false)}
                dataTestId={dataTestId}
              />
            )}
            <div
              className="editor-title-wrapper"
              data-test-id={`${dataTestId}-text-editor-title-wrapper`}
            >
              {title && (
                <span style={{ whiteSpace: 'nowrap' }} className="editor-title">
                  {title}
                </span>
              )}
              {info?.toggle && <InfoTooltip title={info?.desc} />}
            </div>
            <div className="editor_container" style={{ height }}>
              {!editor ? (
                <div className="editor-loading">Editor loading...</div>
              ) : (
                <>
                  <div
                    className="editor_buttons"
                    data-test-id={`${dataTestId}-editor-buttons`}
                  >
                    <div className="button_block">
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-bold`}
                        className={activeStyles.bold ? 'active' : ''}
                        onClick={() =>
                          editor.chain().focus().toggleBold().run()
                        }
                      >
                        <BoldLineIcon width={16} height={16} />
                      </button>
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-italic`}
                        className={activeStyles.italic ? 'active' : ''}
                        onClick={() =>
                          editor.chain().focus().toggleItalic().run()
                        }
                      >
                        <ItalicLineIcon width={16} height={16} />
                      </button>
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-underline`}
                        className={activeStyles.underline ? 'active' : ''}
                        onClick={() =>
                          editor.chain().focus().toggleUnderline().run()
                        }
                      >
                        <UnderlineLineIcon width={16} height={16} />
                      </button>
                    </div>
                    <div className="button_block">
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-bullet-list`}
                        className={activeStyles.bulletList ? 'active' : ''}
                        onClick={() =>
                          editor.chain().focus().toggleBulletList().run()
                        }
                      >
                        <ListLineIcon width={16} height={16} />
                      </button>
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-ordered-list`}
                        className={activeStyles.orderedList ? 'active' : ''}
                        onClick={() =>
                          editor.chain().focus().toggleOrderedList().run()
                        }
                      >
                        <ListOrderedLineIcon width={16} height={16} />
                      </button>
                    </div>
                    <div className="button_block">
                      <button
                        className="input-wrapper"
                        data-test-id={`${dataTestId}-editor-buttons-color-input-wrapper`}
                      >
                        <input
                          data-test-id={`${dataTestId}-editor-buttons-color-input`}
                          type="color"
                          value={activeStyles.color}
                          onChange={(e) =>
                            editor
                              .chain()
                              .focus()
                              .setColor(e.target.value)
                              .run()
                          }
                        />
                      </button>
                      <select
                        value={activeStyles.fontSize}
                        onChange={(e) =>
                          editor
                            .chain()
                            .focus()
                            .setMark('textStyle', { fontSize: e.target.value })
                            .run()
                        }
                        data-test-id={`${dataTestId}-editor-buttons-font-size-select`}
                      >
                        {fontSizes.map((size) => (
                          <option
                            key={size}
                            value={size}
                            data-test-id={`${dataTestId}-editor-buttons-font-size-select-option-${size}`}
                          >
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="button_block">
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-undo`}
                        onClick={() => editor.chain().focus().undo().run()}
                      >
                        <UndoLineIcon width={16} height={16} />
                      </button>
                      <button
                        data-test-id={`${dataTestId}-editor-buttons-redo`}
                        onClick={() => editor.chain().focus().redo().run()}
                      >
                        <RedoLineIcon width={16} height={16} />
                      </button>
                    </div>
                  </div>
                  <EditorContent editor={editor} className="editor" />
                  {hasFullScreen && (
                    <div
                      className="full_screen_icon"
                      onClick={() => {
                        setToggleFullScreenModal(true);
                      }}
                      data-test-id={`${dataTestId}-editor-buttons-full-screen-button`}
                    >
                      <FullScreenOutwardIcon />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        );
      }}
    />
  );
};
